export type ProfileSettingsSectionId =
  | "profile"
  | "business"
  | "documents"
  | "payments"
  | "portfolio"
  | "social"
  | "plan";

export type LegacyProfileSettingsTabKey =
  | "first"
  | "second"
  | "third"
  | "fourth"
  | "fifth"
  | "sixth";

export interface ProfileSettingsSection {
  id: ProfileSettingsSectionId;
  label: string;
}

export const PROFILE_SETTINGS_SECTIONS: Record<
  ProfileSettingsSectionId,
  ProfileSettingsSection
> = {
  profile: {
    id: "profile",
    label: "Profile Basics",
  },
  business: {
    id: "business",
    label: "Business Details",
  },
  documents: {
    id: "documents",
    label: "Documents & Media",
  },
  payments: {
    id: "payments",
    label: "Payments",
  },
  portfolio: {
    id: "portfolio",
    label: "Portfolio",
  },
  social: {
    id: "social",
    label: "Social Links",
  },
  plan: {
    id: "plan",
    label: "Plan",
  },
};

const legacyTabToSection: Record<
  LegacyProfileSettingsTabKey,
  ProfileSettingsSectionId
> = {
  first: "profile",
  second: "business",
  third: "payments",
  fourth: "plan",
  fifth: "portfolio",
  sixth: "social",
};

const sectionToLegacyTab: Record<
  ProfileSettingsSectionId,
  LegacyProfileSettingsTabKey
> = {
  profile: "first",
  business: "second",
  documents: "second",
  payments: "third",
  portfolio: "fifth",
  social: "sixth",
  plan: "fourth",
};

const legacyHashToSection: Record<string, ProfileSettingsSectionId> = {
  "#personalDetails": "profile",
  "#companyDetails": "business",
  "#financial": "payments",
  "#financials": "payments",
  "#recentWork": "portfolio",
  "#packages": "plan",
  "#changePassword": "plan",
};

export function isBusinessProfileUser(userType?: string) {
  return [
    "contractor",
    "admin",
    "subcontractor",
    "real_estate_agent",
    "broker",
  ].includes(userType || "");
}

export function getProfileSettingSections(userType?: string) {
  switch (userType) {
    case "contractor":
    case "admin":
      return [
        PROFILE_SETTINGS_SECTIONS.profile,
        PROFILE_SETTINGS_SECTIONS.business,
        PROFILE_SETTINGS_SECTIONS.documents,
        PROFILE_SETTINGS_SECTIONS.payments,
        PROFILE_SETTINGS_SECTIONS.portfolio,
        PROFILE_SETTINGS_SECTIONS.social,
        PROFILE_SETTINGS_SECTIONS.plan,
      ];
    case "subcontractor":
      return [
        PROFILE_SETTINGS_SECTIONS.profile,
        PROFILE_SETTINGS_SECTIONS.business,
        PROFILE_SETTINGS_SECTIONS.documents,
        PROFILE_SETTINGS_SECTIONS.payments,
        PROFILE_SETTINGS_SECTIONS.portfolio,
        PROFILE_SETTINGS_SECTIONS.social,
      ];
    case "real_estate_agent":
    case "broker":
      return [
        PROFILE_SETTINGS_SECTIONS.profile,
        PROFILE_SETTINGS_SECTIONS.business,
        PROFILE_SETTINGS_SECTIONS.documents,
        PROFILE_SETTINGS_SECTIONS.social,
      ];
    default:
      return [PROFILE_SETTINGS_SECTIONS.profile, PROFILE_SETTINGS_SECTIONS.social];
  }
}

export function normalizeProfileSettingsSection(
  sectionId: ProfileSettingsSectionId,
  userType?: string
) {
  const availableSections = getProfileSettingSections(userType);
  return availableSections.some((section) => section.id === sectionId)
    ? sectionId
    : availableSections[0].id;
}

export function getSectionFromLegacyTab(tabKey?: string) {
  return legacyTabToSection[tabKey as LegacyProfileSettingsTabKey] || "profile";
}

export function getLegacyTabFromSection(sectionId: ProfileSettingsSectionId) {
  return sectionToLegacyTab[sectionId];
}

export function getInitialProfileSettingSection(
  location: Pick<Location, "hash" | "search">,
  userType?: string
) {
  const searchParams = new URLSearchParams(location.search);
  const tag = searchParams.get("tag");
  const payment = searchParams.get("payment");
  const sectionFromHash = legacyHashToSection[location.hash];

  if (tag === "packages") {
    return normalizeProfileSettingsSection("plan", userType);
  }

  if (payment === "success" || payment === "failure") {
    return normalizeProfileSettingsSection("business", userType);
  }

  if (sectionFromHash) {
    return normalizeProfileSettingsSection(sectionFromHash, userType);
  }

  return normalizeProfileSettingsSection("profile", userType);
}
